import { ReadOnlyEntityAttachmentsButton } from '@Components/Attachments';
import { PositionsTable } from '@Components/Tables';
import { BILLING_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { invoiceSnapshotPositionColumns, isCancelledInvoiceSnapshot } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useInvoiceSnapshotAttachments, useInvoiceSnapshotRequisitions } from '@Hooks/Billing';
import { usePrintDocument, usePrintOptions } from '@Hooks/Documents';
import { useBadges } from '@Hooks/useBadges';
import {
    ApiAddress,
    ApiInvoicePositionSnapshot,
    ApiInvoiceSnapshot,
    EnumFrontendLayoutSetting,
    getEnumInvoiceTypeLabel,
    paymentMethodOptions,
} from '@Model';
import { PrintOptionsMenu, PrintOptionsModal } from '@Modules/Documents';
import { InvoiceSnapshotRequisitionButton } from './InvoiceSnapshotRequisitionButton';
import { FrameEditAddress } from '@Modules/Stammdaten';
import { useClientNavigate } from '../../../Routing/useClientNavigate';
import {
    Button,
    Card,
    DateInput,
    defaultIconSize,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    LinkedField,
    ListInfo,
    NumberInput,
    Select,
    TextInput,
    useFrameEditBaseNavigation,
    useLinkedObjectOverlay,
    useTranslate,
    type IFrameBodyProps,
    type IFrameEditShellProps,
} from '@synqo/frontend-component-library';
import { IconDownload } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

const disabled = () => false;

function emptyList<T>(): ListInfo<T> {
    return { items: [], page_count: 0, page_no: 0, page_size: 0, total_item_count: 0 };
}

export function FrameEditInvoiceSnapshot(props: IFrameEditShellProps<ApiInvoiceSnapshot>) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiInvoiceSnapshot>
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            apiResourcePath={`${BILLING_API}/invoice_snapshots`}
            renderer={FrameEditInvoiceSnapshotBody}
            canEdit={false}
            canCreate={false}
            canDelete={false}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditInvoiceSnapshotBody(props: IFrameBodyProps<ApiInvoiceSnapshot>) {
    const i18n = useTranslate('');
    const snapshot = props.data;
    const hasPermission = useHasPermission();

    const printOptionsInfo = usePrintOptions(
        'invoice_snapshot',
        snapshot.id,
        hasPermission(Permission.DocumentsDocumentRead),
    );
    const { printOptions, hasPrintOptions, currentPrintOption, setCurrentPrintOption } = printOptionsInfo;

    const attachmentsInfo = useInvoiceSnapshotAttachments(snapshot.id);
    const showAttachments =
        hasPermission(Permission.BillingInvoiceSnapshotsAttachmentsRead) && attachmentsInfo.attachments.length > 0;

    const printDocument = usePrintDocument();
    const downloadInvoice = useCallback(() => {
        const option = printOptions[0];
        printDocument({ printOption: option.id, primaryId: option.related_object_id, mode: 'download' });
    }, [printDocument, printOptions]);

    const { onClick: openCustomer } = useLinkedObjectOverlay<ApiAddress>({
        Renderer: FrameEditAddress,
        canAdd: false,
    });
    const customerKey = snapshot.customer.foreign_key ?? '';
    const onCustomerClick = useCallback(() => openCustomer(customerKey), [openCustomer, customerKey]);

    // The cancellation is itself an invoice snapshot, so it opens in this same frame.
    const { onClick: openInvoiceSnapshot } = useLinkedObjectOverlay<ApiInvoiceSnapshot>({
        Renderer: FrameEditInvoiceSnapshot,
        canAdd: false,
    });
    const cancellationKey = snapshot.related_cancellation?.foreign_key ?? '';
    const onCancellationClick = useCallback(() => {
        openInvoiceSnapshot(cancellationKey);
    }, [openInvoiceSnapshot, cancellationKey]);
    // Loaded once for the whole frame: the header button needs the distinct requisitions, the position
    // table the requisition of each single order it billed.
    const { requisitions, requisitionByOrderSnapshot } = useInvoiceSnapshotRequisitions(snapshot, true);
    const navigate = useClientNavigate();
    const positionColumns = useMemo(
        () =>
            invoiceSnapshotPositionColumns({
                requisitionByOrderSnapshot,
                onOpenRequisition: (requisitionId) => navigate(`requisitions/${requisitionId}`),
            }),
        [requisitionByOrderSnapshot, navigate],
    );

    const isCancelled = isCancelledInvoiceSnapshot(snapshot);
    const resourceLabel = getEnumInvoiceTypeLabel(snapshot.type);
    const badges = useBadges(snapshot);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel(resourceLabel, props.mode),
                resourceLabel: resourceLabel,
                // Dynamic items first: the header is right-anchored, so late arrivals fill up leftwards and nothing jumps.
                menuItems: [
                    showAttachments && (
                        <ReadOnlyEntityAttachmentsButton key="attachments" attachmentsInfo={attachmentsInfo} />
                    ),
                    hasPrintOptions && (
                        <Button
                            key="download"
                            appearance="transparent"
                            label={i18n('Rechnung herunterladen')}
                            tooltip={i18n('Rechnung herunterladen')}
                            onClick={downloadInvoice}
                        >
                            <IconDownload size={defaultIconSize} />
                        </Button>
                    ),
                    <PrintOptionsMenu key="print_menu" {...printOptionsInfo} />,
                    <InvoiceSnapshotRequisitionButton key="requisition" requisitions={requisitions} />,
                ],
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card
                name="invoiceSnapshotData"
                label="Allgemeine Informationen"
                border="withoutBorder"
                collapsable={false}
                badges={[
                    ...badges,
                    isCancelled && {
                        children: `${i18n('Storniert durch Nr.')} ${snapshot.related_cancellation.display_string}`,
                        variant: 'danger',
                        onClick: onCancellationClick,
                    },
                ]}
            >
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <TextInput
                            name="type"
                            label={i18n('Dokumentenart')}
                            value={getEnumInvoiceTypeLabel(snapshot.type)}
                            onChange={disabled}
                            locked
                        />
                        <LinkedField<ApiAddress>
                            name="customer"
                            label={i18n('Kunde')}
                            value={snapshot.customer.label}
                            linked_key={snapshot.customer.foreign_key}
                            onClick={onCustomerClick}
                            columns={[]}
                            loadLinkableObjects={emptyList}
                            locked
                        />
                    </GridColumns>
                    <GridColumns spans={[4, 4, 4]}>
                        <DateInput
                            type="day"
                            name="date"
                            label={i18n('Rechnungsdatum')}
                            value={snapshot.date}
                            onChange={disabled}
                            locked
                        />
                        <DateInput
                            type="day"
                            name="due_at"
                            label={i18n('Fällig am')}
                            value={snapshot.due_at}
                            onChange={disabled}
                            locked
                        />
                        <DateInput
                            type="date"
                            name="payment_completed_at"
                            label={i18n('Bezahlt am')}
                            value={snapshot.payment_completed_at ?? ''}
                            onChange={disabled}
                            locked
                        />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <NumberInput
                            name="total_net_price"
                            label={i18n('Netto in €')}
                            value={snapshot.total_net_price}
                            onChange={disabled}
                            decimals={2}
                            locked
                        />
                        <NumberInput
                            name="total_gross_price"
                            label={i18n('Brutto in €')}
                            value={snapshot.total_gross_price}
                            onChange={disabled}
                            decimals={2}
                            locked
                        />
                    </GridColumns>
                </Grid>
            </Card>

            <Card name="payment_terms" label={i18n('Zahlungsbedingungen')} gap="md">
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <Select
                            name="payment_method"
                            value={snapshot.payment_terms.payment_method}
                            items={paymentMethodOptions}
                            label={i18n('Zahlungsart')}
                            onChange={disabled}
                            locked
                        />
                        <NumberInput
                            name="due_days"
                            value={snapshot.payment_terms.due_days}
                            onChange={disabled}
                            label={i18n('Fälligkeit (Tage)')}
                            locked
                        />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <NumberInput
                            name="discount1_days"
                            value={snapshot.payment_terms.discount1_days}
                            onChange={disabled}
                            label={i18n('Skonto 1 (Tage)')}
                            locked
                        />
                        <NumberInput
                            name="discount1_percentage"
                            value={snapshot.payment_terms.discount1_percentage}
                            onChange={disabled}
                            label={i18n('Skonto 1 (%)')}
                            locked
                        />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <NumberInput
                            name="discount2_days"
                            value={snapshot.payment_terms.discount2_days}
                            onChange={disabled}
                            label={i18n('Skonto 2 (Tage)')}
                            locked
                        />
                        <NumberInput
                            name="discount2_percentage"
                            value={snapshot.payment_terms.discount2_percentage}
                            onChange={disabled}
                            label={i18n('Skonto 2 (%)')}
                            locked
                        />
                    </GridColumns>
                </Grid>
            </Card>

            <Card name="positions" label={`${i18n('Positionen')} (${snapshot.positions.length})`}>
                <PositionsTable<ApiInvoicePositionSnapshot>
                    layoutSettingsContext={EnumFrontendLayoutSetting.InvoiceSnapshotPositionsTableLayout}
                    items={snapshot.positions}
                    columns={positionColumns}
                    name="InvoiceSnapshotPositions"
                    editable={false}
                    canCreate={false}
                    onAdd={() => undefined}
                    disableTopToolbar
                />
            </Card>

            <PrintOptionsModal printOption={currentPrintOption} onClose={() => setCurrentPrintOption(undefined)} />
        </EditFrameShell>
    );
}
